import { useEffect, useRef, useState } from 'react';
import {
  Button,
  Carousel,
  CarouselApi,
  CarouselContent,
  CarouselFooter,
  CarouselIndicator,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from '@qodo/design-system';
import { Download } from 'lucide-react';
import { Step } from 'shared/InstallationDialog';
import { useMultiStepDialog } from 'shared/MultiStepDialog';
import { Checkbox } from 'ui/checkbox';
import { downloadFile } from 'utils/downloadFile';
import { spawn } from 'utils/translations';
import { QODO_LOGO_DOWNLOAD_URL } from '../consts';
import { getSubSteps } from './createBotUserConfig';

const spawnCreate = spawn('dialogs.bitbucket-installation.create-bot-user');

export const CreateBotUser = () => {
  const [carouselApi, setCarouselApi] = useState<CarouselApi>();
  const [currentSlideIndex, setCurrentSlideIndex] = useState(0);
  const [confirmed, setConfirmed] = useState(false);
  const indicatorsRef = useRef<HTMLDivElement>(null);
  const { setIsNextEnabled } = useMultiStepDialog();

  const subSteps = getSubSteps();

  useEffect(() => {
    setIsNextEnabled(confirmed);
  }, [confirmed, setIsNextEnabled]);

  useEffect(() => {
    if (!carouselApi) return;

    setCurrentSlideIndex(carouselApi.selectedScrollSnap());

    const onSelect = () =>
      setCurrentSlideIndex(carouselApi.selectedScrollSnap());

    carouselApi.on('select', onSelect);

    return () => {
      carouselApi.off('select', onSelect);
    };
  }, [carouselApi]);

  useEffect(() => {
    const container = indicatorsRef.current;

    if (!container) return;

    const active = container.children[currentSlideIndex] as
      | HTMLElement
      | undefined;

    active?.scrollIntoView({
      block: 'nearest',
      inline: 'nearest',
      behavior: 'smooth',
    });
  }, [currentSlideIndex]);

  const renderCarouselItem = (
    step: { image: string; alt: string },
    index: number,
  ) => (
    <CarouselItem key={index} className="basis-full">
      <img
        src={step.image}
        alt={step.alt}
        className="rounded-md w-full h-78 object-contain"
      />
    </CarouselItem>
  );

  const renderCarouselIndicator = (step: { label: string }, index: number) => (
    <CarouselIndicator
      key={index}
      index={index}
      isCurrent={index === currentSlideIndex}
      className="whitespace-nowrap h-auto py-1 uppercase"
    >
      {index + 1}. {step.label}
    </CarouselIndicator>
  );

  return (
    <Step title={spawnCreate('title')} description={spawnCreate('description')}>
      <Button
        variant="outline"
        size="sm"
        icon={Download}
        iconPosition="right"
        onClick={() => downloadFile(QODO_LOGO_DOWNLOAD_URL, 'qodo-logo.png')}
        className="w-fit"
      >
        {spawnCreate('download-logo')}
      </Button>

      <div className="bg-fill-interactive-neutral-idle rounded-lg flex flex-col gap-4 pb-5 pt-4 px-5">
        <p className="text-text-general-neutral-mild text-xs tracking-wide">
          {spawnCreate('steps-note')}
        </p>
        <Carousel setApi={setCarouselApi} className="w-full">
          <CarouselContent>{subSteps.map(renderCarouselItem)}</CarouselContent>
          <CarouselFooter>
            <CarouselPrevious type="button" />
            <div
              ref={indicatorsRef}
              className="flex items-center font-mono text-xs overflow-x-hidden"
            >
              {subSteps.map(renderCarouselIndicator)}
            </div>
            <CarouselNext type="button" />
          </CarouselFooter>
        </Carousel>
      </div>

      <div className="flex items-center justify-end">
        <label className="flex items-center gap-2 cursor-pointer">
          <Checkbox
            checked={confirmed}
            onCheckedChange={(checked) => setConfirmed(Boolean(checked))}
          />
          <span className="text-sm text-text-general-neutral-mid font-medium">
            {spawnCreate('confirm-account-created')}
          </span>
        </label>
      </div>
    </Step>
  );
};
